/* eslint-disable camelcase */
import { RcFile } from "antd/es/upload";
import { isEmpty, isNil, map } from "ramda";
import { Key } from "react";

import {
  AIPodcardFormDataType,
  GenderEnum,
  IAudioScript,
  IDigital,
  IDigitalGroup,
  IDigitalGroupTaskStatus,
  IDigitalImage,
  IFaceDetectionResult,
  IGenerateAudiotParams,
  IGenerateScriptParams,
  IGenerateToneParams,
  IPodcastSettings,
  ISystemDigitalGroup,
  ISystemDigitalImage,
  ITryListenParams,
  IVoice,
  TransformedDefaultTone,
} from "@/types/ai-podcard";
import { IResolve } from "@/types/communal";
import { snakeToCamel } from "@/utils/field-name-conversion";

import { api } from "./api";

/**
 * 生成脚本接口
 * @description 根据附件内容和参数生成 AI 播客脚本
 * @param params - 生成脚本所需的参数
 * @param params.attachmentPath - 附件路径
 * @param params.attachmentName - 附件名称
 * @param params.podcastContent - 播客内容描述
 * @param params.duration - 期望时长（分钟）
 * @param params.language - 语言类型
 * @param params.voices - 音色配置数组
 * @returns 返回草稿 ID 和任务 ID
 */
export const generateScriptApi = async (params: IGenerateScriptParams) => {
  const response = await api.post(`/podcast/script/generate`, {
    attachment_path: params.attachmentPath,
    attachment_name: params.attachmentName,
    podcast_content: params.podcastContent,
    content_type: params.contentType,
    duration: params.duration,
    language: params.language,
    voices: map((voice) => {
      return {
        ...voice,
        language_id: voice.languageId,
        response_format: voice.responseFormat,
      };
    }, params.voices),
    type: params.type,
    image_group: params.imageGroupId,
    enable_caption: params.enableCaption ? 1 : 0,
    caption_language: params.captionLanguage ?? 0,
    script: params.script,
  });

  return {
    ...response,
    data: { draftId: response.data.draft_id, taskId: response.data.task_id },
  };
};

/**
 * 获取生成脚本状态接口
 * @description 查询脚本生成任务的当前状态
 * @param id - 任务 ID
 * @returns 返回任务状态信息和字数范围
 */
export const getGenerateScriptStatusApi = async (id: string) => {
  const response = await api.get(`/podcast/script/task/${id}/status`);

  return { ...response.data, lengthRange: response.data.length_range };
};

/**
 * 通过对话更新脚本接口
 * @description 根据用户输入的文本内容更新脚本
 * @param params - 更新参数
 * @param params.script - 当前脚本数组
 * @param params.text - 用户输入的文本
 * @param params.duration - 时长
 * @param params.language - 语言类型
 * @param params.voices - 音色配置数组
 * @returns 返回更新后的脚本数据
 */
export const updateScriptByTextApi = async (params: {
  script: IAudioScript[];
  text: string;
  duration: number;
  language: number;
  voices: IVoice[];
}) => {
  const response = await api.post(`/podcast/script/generate-by-dialog`, {
    ...params,
    voices: map((voice) => {
      return {
        ...voice,
        language_id: voice.languageId,
        response_format: voice.responseFormat,
      };
    }, params.voices),
  });

  return response.data;
};

/**
 * 更新脚本接口
 * @description 直接更新指定任务的脚本内容
 * @param id - 任务 ID
 * @param script - 新的脚本数组
 * @returns 返回更新结果
 */
export const updateScriptApi = async (id: string, script: IAudioScript[]) => {
  const res = await api.put(`/podcast/task/${id}/script`, { script });

  return res.data;
};

/**
 * 获取音频配额接口
 * @description 查询用户剩余的音频生成配额
 * @returns 返回配额信息
 */
export const getAudioQuotaApi = async () => {
  const { data } = await api.get(`/podcast/audio/quota`);

  return data;
};

/**
 * 生成音频接口
 * @description 根据脚本和参数生成 AI 播客音频
 * @param params - 生成音频所需的参数
 * @param params.podcastContent - 播客内容
 * @param params.attachmentPath - 附件路径
 * @param params.attachmentName - 附件名称
 * @param params.voices - 音色配置数组
 * @returns 返回生成的音频数据
 */
export const generateAudioApi = async (params: IGenerateAudiotParams) => {
  const response = await api.post(`/podcast/file/generate`, {
    ...params,
    podcast_content: params.podcastContent,
    attachment_path: params.attachmentPath,
    attachment_name: params.attachmentName,
    voices: map((voice) => {
      return {
        ...voice,
        language_id: voice.languageId,
        response_format: voice.responseFormat,
      };
    }, params.voices),
    image_url: params.imageGroupUrl,
    enable_caption: params.enableCaption ? 1 : 0,
    caption_language: params.captionLanguage ?? 0,
  });

  return response.data;
};

/**
 * 获取生成音频进度接口
 * @description 查询音频生成任务的进度状态
 * @param id - 任务 ID
 * @returns 返回任务状态、进度、等待时间和文件 URL
 */
export const getGenerateAudioProgressApi = async (id: string) => {
  const response = await api.get(`/podcast/task/${id}/status`);

  return {
    data: {
      status: response.data.status,
      progress: response.data.progress,
      pendingTime: response.data.pending_time,
      fileUrl: response.data.file_url,
      cover: response.data.cover,
    },
  };
};

/**
 * 删除草稿接口
 * @description 删除当前的播客草稿
 * @returns 返回删除结果
 */
export const deleteDraftApi = async () => {
  const response = await api.delete(`/podcast/draft`);

  return response;
};

/**
 * 获取草稿接口
 * @description 获取当前用户的播客草稿数据
 * @returns 返回草稿数据，如果不存在则返回 null
 */
export const getDraftApi = async (): Promise<AIPodcardFormDataType | null> => {
  const res = await api.get(`/podcast/draft`);

  return isNil(res.data) || isEmpty(res.data)
    ? null
    : {
        id: res.data.id,
        type: res.data.type, // 音频索引
        attachmentName: res.data.attachment_name,
        attachmentPath: res.data.attachment_path,
        duration: res.data.duration,
        language: res.data.language,
        hostTimbre: res.data.host_timbre,
        guestTimbre: res.data.guest_timbre,
        content: res.data.content,
        status: res.data.status,
        progress: res.data.progress,
        fileUrl: res.data.file_url,
        contentType: res.data.content_type,
        script: isNil(res.data.script)
          ? null
          : res.data.script.map((script: IAudioScript, index: number) => {
              return { ...script, id: index };
            }),
        wordCountRange: res.data.length_range,
        isSettingChanged: res.data.is_setting_changed,
        scriptTaskId: res.data.script_task_id,
        scriptTaskStatus: res.data.script_task_status,
        taskId: res.data.task_id,
        pendingTime: res.data.pending_time,
        imageGroupId: res.data.image_group,
        enableCaption: res.data.enable_caption,
        captionLanguage: res.data.caption_language,
        cover: res.data.cover,
      };
};

/**
 * 更新草稿接口
 * @description 更新指定草稿的任务信息
 * @param id - 草稿 ID
 * @param params - 更新参数（不包含 voices）
 * @returns 返回更新后的草稿数据
 */
export const updateDraftApi = async (
  id: string,
  params: Omit<IGenerateScriptParams, "voices">
): Promise<AIPodcardFormDataType> => {
  const res = await api.put(`/podcast/task/${id}`, {
    type: params.type,
    content_type: params.contentType,
    attachment_path: params.attachmentPath,
    attachment_name: params.attachmentName,
    podcast_content: params.podcastContent,
    duration: params.duration,
    language: params.language,
    host_timbre: params.hostTimbre ?? null,
    guest_timbre: params.guestTimbre ?? null,
    is_setting_changed: params.isSettingChanged,
    image_group: params.imageGroupId ?? null,
    enable_caption: params.enableCaption,
    caption_language: params.captionLanguage,
  });

  return res.data;
};

/**
 * 提交草稿接口
 * @description 提交当前草稿进行保存或发布
 * @returns 返回提交结果
 */
export const submitDraftApi = async () => {
  const response = await api.put(`/podcast/draft/commit`, {});

  return response;
};

/**
 * 获取音色列表接口
 * @description 获取系统音色和个人音色列表
 * @returns 返回系统音色和个人音色数组
 */
export const getToneListApi = async (languageType?: number) => {
  const { data } = await api.get(`/personal/ea2/timbre`, {
    params: { language_type: languageType },
  });

  return {
    data: {
      systemTone: map(
        (item) => ({
          id: item.id,
          languageType: item.language_type,
          title: item.name,
          gender: item.gender,
        }),
        data.speech_list
      ),
      personalTone: map(
        (item) => ({
          id: item.id,
          languageType: item.material_language,
          title: item.name,
          source: item.source,
        }),
        data.ea_voices
      ),
    },
    message: "",
    status: 1,
  };
};

/**
 * 获取音色进度接口
 * @description 查询个人音色队列数量
 * @returns 返回队列数量信息
 */
export const getToneProgressApi = async () => {
  const { data } = await api.get(`/personal/ea2/timbre/queue/count`);

  return {
    data: {
      queueingCount: data.queueing_count,
      inProgressCount: data.in_progress_count,
    },
  };
};

/**
 * 获取默认音色接口
 * @description 获取系统默认音色列表
 * @returns 返回默认音色列表
 */
export const getDefaultToneListApi = async (): Promise<
  IResolve<TransformedDefaultTone>
> => {
  const { data } = await api.get(`/personal/ea2/timbre/default`);

  return {
    data: {
      manM: data.video?.["man_m"],
      canM: data.video?.["can_m"],
      manF: data.video?.["man_f"],
      canF: data.video?.["can_f"],
      man: data.audio?.man,
      can: data.audio?.can,
    },
    message: "",
    status: 1,
  };
};

/**
 * 音色检测接口
 * @description 对提供的音频进行音色检测分析
 * @param url - 音频文件 URL
 * @returns 返回检测结果
 */
export const timbreDetectionApi = async (url: string) => {
  const response = await api.post(`personal/timbre/sample/detect`, { url });

  return response;
};

/**
 * 上传音频素材接口
 * @description 上传音频素材进行分析处理
 * @param params - 上传参数
 * @param params.materialLanguage - 素材语言
 * @param params.fileUrl - 文件 URL
 * @param params.fileName - 文件名称
 * @param params.fileSizeBytes - 文件大小（字节）
 * @returns 返回上传结果
 */
export const uploadAudioMaterialApi = async (params: {
  materialLanguage: number;
  fileUrl: string;
  fileName: string;
  fileSizeBytes: number;
}) => {
  const response = await api.post(`personal/ea2/timbre/material/upload`, {
    material_language: params.materialLanguage,
    file_url: params.fileUrl,
    file_name: params.fileName,
    file_size_bytes: params.fileSizeBytes,
  });

  return {
    data: {
      materialText: response.data.material_text,
    },
    message: "",
    status: 1,
  };
};

/**
 * 提交自定义音色接口
 * @description 提交用户自定义的音色样本
 * @param params - 自定义音色参数
 * @param params.fileName - 文件名称
 * @param params.fileUrl - 文件 URL
 * @param params.materialText - 素材文本
 * @param params.callbackUrl - 回调地址
 * @param params.fileSizeBytes - 文件大小
 * @param params.materialLanguage - 素材语言
 * @param params.name - 音色名称
 * @param params.source - 来源
 * @returns 返回提交结果
 */
export const submitCustomizedToneApi = async (params: IGenerateToneParams) => {
  const response = await api.post(`/personal/ea2/timbre`, {
    file_name: params.fileName,
    file_url: params.fileUrl,
    material_text: params.materialText,
    file_size_bytes: params.fileSizeBytes,
    material_language: params.materialLanguage,
    name: params.name,
    source: params.source,
  });

  return response;
};

/**
 * 删除我的音色接口
 * @description 删除用户自定义的音色
 * @param id - 音色 ID
 * @returns 返回删除结果
 */
export const deleteMyToneApi = async (id: Key) => {
  const response = await api.delete(`/personal/ea2/timbre/${id}`);

  return response;
};

/**
 * 系统音色试听接口
 * @description 使用指定音色试听文本内容
 * @param params - 试听参数
 * @param params.voiceId - 音色 ID
 * @returns 返回试听结果
 */
export const systemTryListenApi = async (params: ITryListenParams) => {
  const response = await api.post(`/personal/timbre/test`, {
    ...params,
    voice_id: params.voiceId,
    isSystem: true,
  });

  return response;
};

/**
 * 定制音色试听接口
 * @description 使用指定音色试听文本内容
 * @param params - 试听参数
 * @param params.voiceId - 音色 ID
 * @returns 返回试听结果
 */
export const tryListenApi = async (params: ITryListenParams) => {
  const response = await api.post(`/personal/ea2/timbre/test`, {
    ...params,
    voice_id: params.voiceId,
  });

  return response;
};

/**
 * 获取播客权限接口
 * @description 检查用户是否有使用播客功能的权限
 * @returns 返回权限信息
 */
export const getPodcastPermissionApi = async () => {
  const response = await api.get(`/podcast/permission`);

  return response;
};

/**
 * 上传附件接口
 * @description 上传播客制作所需的附件文件
 * @param data - 上传数据
 * @param data.file - 要上传的文件
 * @returns 返回上传后的文件 URL
 */
export const uploadAttachmentApi = async (data: { file: File }) => {
  const formData = new FormData();

  formData.append("file", data.file);
  const response = await api.post(`/podcast/attachment/upload`, formData);

  return response.data.url[0] as string;
};

/**
 * 人像检测接口
 * @description 检测图片中是否包含人脸
 * @param image - 待检测的图片 Blob 对象
 * @returns 返回人脸检测结果
 */
export const detectDigitalApi = async (
  image: RcFile
): Promise<IFaceDetectionResult> => {
  // 构建 FormData 处理文件上传
  const formData = new FormData();

  formData.append("image", image);

  const { data } = await api.post(`/digital/detect`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return { imageUrl: data.image_url, status: data.status };
};

/**
 * 生成数字人像接口
 * @description 根据图片 URL 生成数字人像
 * @param imageUrl - 图片 URL
 * @returns 返回生成的数字人像信息
 */
export const generateDigitalApi = async (
  imageUrl: string
): Promise<IDigital> => {
  const { data } = await api.post(`/digital/generate`, { image_url: imageUrl });

  return {
    imageUrl: data.image_url,
    gender: data.gender,
  };
};

/**
 * 获取数字人像列表接口
 * @description 获取用户已创建的数字人像列表
 * @returns 返回数字人像数组
 */
export const getDigitalListApi = async (): Promise<IDigitalImage[]> => {
  const response = await api.get(`/digital/list`);

  return snakeToCamel(response.data);
};

/**
 * 添加数字人像接口
 * @description 添加新的数字人像记录
 * @param data - 数字人像数据
 * @returns 返回添加结果
 */
export const addDigitalApi = async (data: IDigital) => {
  const response = await api.post(`/digital`, data);

  return response.data;
};

/**
 * 删除数字人像接口
 * @description 删除指定的数字人像
 * @param imageId - 数字人像 ID
 * @returns 返回删除结果
 */
export const deleteDigitalApi = async (imageId: string) => {
  const response = await api.delete(`/digital/${imageId}`);

  return response.data;
};

/**
 * 获取系统数字人像列表接口
 * @description 获取系统提供的默认数字人像列表
 * @returns 返回系统数字人像数组
 */
export const getSystemDigitalListApi = async (): Promise<
  ISystemDigitalImage[]
> => {
  const response = await api.get(`/digital/system/list`);

  return snakeToCamel(response.data);
};

/**
 * 生成数字人像组合接口
 * @description 创建主持人和嘉宾的数字人像组合
 * @param data - 生成参数
 * @param data.hostImageUrl - 主持人图片 URL
 * @param data.guestImageUrl - 嘉宾图片 URL
 * @param data.hostGender - 主持人性别
 * @param data.guestGender - 嘉宾性别
 * @param data.style - 系统数字人像样式（选择系统数字人像时传递）
 * @returns 返回任务 ID 和预计耗时
 */
export const generateDigitalGroupApi = async (params: {
  hostImageUrl: string;
  guestImageUrl: string;
  hostGender: GenderEnum;
  guestGender: GenderEnum;
  style?: string;
}): Promise<IDigitalGroupTaskStatus> => {
  const { data } = await api.post(`/digital/group`, {
    host_image_url: params.hostImageUrl,
    guest_image_url: params.guestImageUrl,
    host_gender: params.hostGender,
    guest_gender: params.guestGender,
    style: params.style,
  });

  return { id: data.id, taskId: data.task_id, time: data.time };
};

/**
 * 获取数字人像组合列表接口
 * @description 获取用户已创建的数字人像组合列表
 * @returns 返回数字人像组合数组
 */
export const getDigitalGroupListApi = async (): Promise<IDigitalGroup[]> => {
  const response = await api.get(`/digital/group/list`);

  return snakeToCamel(response.data);
};

/**
 * 删除数字人像组合接口
 * @description 删除指定的数字人像组合
 * @param groupId - 组合 ID
 * @returns 返回删除结果
 */
export const deleteDigitalGroupApi = async (groupId: string) => {
  const response = await api.delete(`/digital/group/${groupId}`);

  return response.data;
};

/** 人像组合任务状态响应数据 */

/**
 * 获取人像组合生成任务状态接口
 * @description 查询数字人像组合生成任务的当前状态
 * @param taskId - 任务 ID
 * @returns 返回任务 ID 和任务状态
 */
export const getDigitalGroupTaskStatusApi = async (
  taskId: string
): Promise<IDigitalGroupTaskStatus> => {
  const { data } = await api.get(`/digital/group/task/${taskId}`);

  return {
    ...data,
    taskId: data.task_id,
    pendingTime: data.pending_time,
  };
};

/**
 * 获取系统数字人像组合列表接口
 * @description 获取系统提供的默认数字人像组合列表
 * @returns 返回系统数字人像组合数据
 */
export const getSystemDigitalGroupListApi = async (): Promise<
  ISystemDigitalGroup[]
> => {
  const response = await api.get(`/digital/system/group/list`);

  return snakeToCamel(response.data);
};

/**
 * 提交数字人像组合接口
 * @description 提交用户创建的数字人像组合
 * @param data - 提交参数
 * @param data.groupId - 组合 ID
 * @returns 返回提交结果
 */
export const submitDigitalGroupApi = async (
  groupId: string
): Promise<{ status: number }> => {
  const response = await api.post(`/digital/group/${groupId}`);

  return response.data;
};

/**
 * 提交数字人像接口
 * @description 提交用户创建的数字人像
 * @param data - 提交参数
 * @param data.id - 数字人像 ID
 * @returns 提交结果
 */
export const submitDigitalApi = async (data: {
  gender: GenderEnum;
  imageUrl: string;
  name: string;
}): Promise<{ status: number }> => {
  const response = await api.post(`/digital`, {
    gender: data.gender,
    image_url: data.imageUrl,
    name: data.name,
  });

  return response.data;
};

/**
 * 获取播客设置
 * @returns 播客设置
 */
export const getPodcastSettingsApi = async (): Promise<IPodcastSettings> => {
  const { data } = await api.get(`/podcast/settings`);

  return {
    podcastAudioQuota: data.podcast_audio_quota,
    podcastPermission: data.podcast_permission,
    podcastVideoQuota: data.podcast_video_quota,
    timbreCount: data.timbre_count,
    timbreCountLimit: data.timbre_count_limit,
    digitalImageLimit: data.digital_image_limit,
    digitalImage: data.digital_image,
    digitalImageGroup: data.digital_image_group,
  };
};
